import type { Nullable } from "@/types/utils";

export enum CompanyQuestionTemplateAssessmentResponseFormat {
  VoiceRecommended = "voice_recommended",
  VoiceRequired = "voice_required",
  Written = "written",
}

export enum CompanyQuestionTemplateBasicQuestionInputType {
  Email = "email",
  FileUpload = "file_upload",
  LongText = "long_text",
  MultipleChoice = "multiple_choice",
  Name = "name",
  Numerical = "numerical",
  Phone = "phone",
  SingleChoice = "single_choice",
  Text = "text",
  Voice = "voice",
  YesNo = "yes_no",
}

export interface CompanyManageListItem {
  id: string;
  isDeleted: boolean;
  name: string;
  userCount: number;
}

export interface CompanyManageDetail {
  defaultClosingStatement: Nullable<string>;
  defaultOpeningStatement: Nullable<string>;
  id: string;
  isDeleted: boolean;
  name: Nullable<string>;
}

export interface UpdateCompanyManagePayload {
  defaultClosingStatement: Nullable<string>;
  defaultOpeningStatement: Nullable<string>;
  name: string;
}

export type UpdateCompanyManageMutationPayload = UpdateCompanyManagePayload & {
  companyId: string;
};

export interface CompanyQuestionTemplateOption {
  id?: string;
  optionOrder: number;
  optionText: string;
  optionValue: string;
  qualifyingExpression?: unknown | null;
}

export interface CompanyQuestionTemplateBasicQuestion {
  id?: string;
  inputType?: CompanyQuestionTemplateBasicQuestionInputType;
  internalLabel?: Nullable<string>;
  isIncluded?: boolean;
  isQualifying?: boolean;
  maxFollowups?: number;
  options?: CompanyQuestionTemplateOption[];
  qualifyingExpression?: unknown | null;
  questionOrder: number;
  questionText: Nullable<string>;
  showInSummary?: boolean;
}

export interface CompanyQuestionTemplateAssessmentQuestion {
  canSkip?: boolean;
  id?: string;
  isCustom?: boolean;
  isSelected?: boolean;
  maxFollowups?: number;
  questionOrder: number;
  questionText: string;
  recordingDurationSeconds?: number;
  responseFormat?: CompanyQuestionTemplateAssessmentResponseFormat;
}

export interface CompanyQuestionTemplateCompetency {
  assessmentQuestions?: CompanyQuestionTemplateAssessmentQuestion[];
  definition: string;
  displayName: string;
  hasCustomScoringGuidelines?: boolean;
  id?: string;
  isCustom?: boolean;
  isIncluded?: boolean;
  name: string;
  scoringGuidelines?: unknown | null;
  sortOrder: number;
  warningBypassLog?: unknown | null;
}

export interface CompanyQuestionTemplate {
  basicQuestions: CompanyQuestionTemplateBasicQuestion[];
  closingStatement: Nullable<string>;
  competencies: CompanyQuestionTemplateCompetency[];
  id: string;
  openingStatement: Nullable<string>;
}

export type UpsertCompanyQuestionTemplateOption = Omit<
  CompanyQuestionTemplateOption,
  "id" | "optionOrder"
>;

export type UpsertCompanyQuestionTemplateBasicQuestion = Omit<
  CompanyQuestionTemplateBasicQuestion,
  "id" | "options" | "questionOrder"
> & {
  options?: UpsertCompanyQuestionTemplateOption[];
};

export type UpsertCompanyQuestionTemplateAssessmentQuestion = Omit<
  CompanyQuestionTemplateAssessmentQuestion,
  "id" | "questionOrder"
>;

export type UpsertCompanyQuestionTemplateCompetency = Omit<
  CompanyQuestionTemplateCompetency,
  "assessmentQuestions" | "id" | "sortOrder"
> & {
  assessmentQuestions?: UpsertCompanyQuestionTemplateAssessmentQuestion[];
};

export interface UpsertCompanyQuestionTemplatePayload {
  basicQuestions: UpsertCompanyQuestionTemplateBasicQuestion[];
  closingStatement: Nullable<string>;
  competencies: UpsertCompanyQuestionTemplateCompetency[];
  openingStatement: Nullable<string>;
}

export type UpsertCompanyQuestionTemplateMutationPayload =
  UpsertCompanyQuestionTemplatePayload & {
    companyId: string;
  };

export interface UpdateCompanyDefaultsManagePayload {
  company: UpdateCompanyManagePayload;
  questionTemplate: UpsertCompanyQuestionTemplatePayload;
}

export type UpdateCompanyDefaultsManageMutationPayload =
  UpdateCompanyDefaultsManagePayload & {
    companyId: string;
  };

export interface CompanyDefaultsManageResponse {
  company: CompanyManageDetail;
  questionTemplate: CompanyQuestionTemplate;
}

export interface DraftCompany {
  isDraft: true;
  name: string;
  tempId: string;
}

export type ExistingCompanyRow = CompanyManageListItem & { isDraft: false };

export type CompanyManagementTableRow = DraftCompany | ExistingCompanyRow;

export interface CompanyManagementTableMeta {
  activeCompanyId: Nullable<string>;
  isAddPending: boolean;
  isOperationInProgress: boolean;
  onAdd: (name: string) => void;
  onCancelAdd: () => void;
  onDeactivate: (companyId: string) => void;
  onEdit: (companyId: string) => void;
  onReactivate: (companyId: string) => void;
  onUpdateDraft: (name: string) => void;
}
